import { PRODUCT_AVAILABILITY, getProductAvailabilityStatus } from "../constants/availability";
import { isPublicFarmStand, productMatchesFarmStand } from "./farmStandPublic";
import { normalizeCoordinates, normalizeLocationRecord } from "./locationUtils";
import { getStandPublicLocationLabel } from "./standLocation";

const PUBLIC_MARKETPLACE_STATUSES = new Set(["active", "open"]);
const HIDDEN_MARKETPLACE_STATUSES = new Set([
  "archived",
  "deleted",
  "hidden",
  "inactive",
  "private",
  "sold",
  "sold_out",
  "out_of_stock",
]);
const VISIBLE_MARKETPLACE_AVAILABILITY = new Set([
  PRODUCT_AVAILABILITY.AVAILABLE_NOW,
  PRODUCT_AVAILABILITY.COMING_SOON,
  PRODUCT_AVAILABILITY.MADE_TO_ORDER,
]);

export const isActivePublicMarketplaceListing = (product = {}) => {
  if (!product || typeof product !== "object") return false;

  const status = String(product.status || "").trim().toLowerCase();
  const visibility = String(product.visibility || product.privacy || "").trim().toLowerCase();
  const availability = getProductAvailabilityStatus(product);

  return (
    PUBLIC_MARKETPLACE_STATUSES.has(status) &&
    !HIDDEN_MARKETPLACE_STATUSES.has(status) &&
    product.isArchived !== true &&
    product.archived !== true &&
    product.isDeleted !== true &&
    product.deleted !== true &&
    product.isHidden !== true &&
    product.hidden !== true &&
    product.isPrivate !== true &&
    product.private !== true &&
    product.outOfStock !== true &&
    product.isPublic !== false &&
    visibility !== "private" &&
    VISIBLE_MARKETPLACE_AVAILABILITY.has(availability)
  );
};

export const getMarketplaceProductLocation = (product = {}) =>
  normalizeLocationRecord({
    ...product.location,
    lat: product.lat ?? product.location?.lat ?? product.standLat,
    lng: product.lng ?? product.location?.lng ?? product.standLng,
    city: product.city || product.location?.city || product.standCity || "",
    state: product.state || product.location?.state || product.standState || "",
    county: product.county || product.location?.county || "",
    locationText:
      product.locationText ||
      product.location?.locationText ||
      product.standLocationText ||
      "",
  });

const getMarketplaceLocationLabel = (product = {}, loc = {}) =>
  loc.city || loc.state
    ? [loc.city, loc.state].filter(Boolean).join(", ")
    : loc.locationText || product.locationText || "";

const canUseLinkedStandLocation = (stand = {}) =>
  isPublicFarmStand(stand) &&
  stand.isPublic !== false &&
  stand.visibility !== "private";

export const getMarketplaceProductLinkedStand = (product = {}, stands = []) => {
  if (!product || !Array.isArray(stands)) return null;

  return (
    stands.find((stand) => canUseLinkedStandLocation(stand) && productMatchesFarmStand(product, stand)) ||
    null
  );
};

export const getMarketplaceProductDisplayLocationLabel = (
  product = {},
  linkedStand = null
) => {
  const productLocationLabel = getMarketplaceLocationLabel(
    product,
    getMarketplaceProductLocation(product)
  );
  if (productLocationLabel) return productLocationLabel;

  if (linkedStand && canUseLinkedStandLocation(linkedStand)) {
    return getStandPublicLocationLabel(linkedStand);
  }

  return "Location not listed";
};

export const getMarketplaceProductMapPoint = (product = {}) =>
  normalizeCoordinates(getMarketplaceProductLocation(product));

export const isMarketplaceListingMapEligible = (product = {}) =>
  isActivePublicMarketplaceListing(product) &&
  Boolean(getMarketplaceProductMapPoint(product));

export const getMarketplaceProductMapType = (product = {}) =>
  product.productType || product.plantCategory || "product";
